import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineItem, DinePair, PairInput } from 'api/types';

/**
 * "Goes well with" (spec 8.7) — for a dish (itemId) or a whole menu group
 * (groupId). Up to four companion dishes, in order, each on or off.
 *
 * A dish with none of its own follows its menu group; its tab shows the
 * group's list read-only with a note. Adding a row to the dish gives it its own
 * list; clearing them all goes back to the group's. The panel saves itself:
 * "Save companions" is its own button, so the dialog's footer has nothing to do.
 *
 * This is a PROMPT. Nothing here adds to an order (BR-036); the waiter taps.
 */
interface Props {
  itemId?: string;
  groupId?: string;
  /** For a dish: what its tab shows about the group it follows. */
  onChanged?: () => void;
}

const MAX = 4;

export const DineOpsPairPanel: React.FC<Props> = ({ itemId, groupId, onChanged }) => {
  const [rows, setRows] = useState<DinePair[]>([]);
  const [groupRows, setGroupRows] = useState<DinePair[]>([]);
  const [groupName, setGroupName] = useState('');
  const [effective, setEffective] = useState<DinePair[]>([]);
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<DineItem[]>([]);
  const [searching, setSearching] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (itemId) {
        const r = await dineOpsService.getItemPairs(itemId);
        setRows(r.own); setGroupRows(r.group); setGroupName(r.groupName); setEffective(r.effective);
      } else if (groupId) {
        setRows(await dineOpsService.getGroupPairs(groupId));
      }
      setDirty(false);
    } catch {
      notificationService.error('Could not load', 'The companions could not be loaded. Try again.');
    } finally {
      setLoading(false);
    }
  }, [itemId, groupId]);

  useEffect(() => { void load(); }, [load]);

  // Search as you type; the API caps at 50 and we show 8. The dish itself and
  // dishes already listed are not offered again.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setHits([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      dineOpsService.searchItems(q, 20)
        .then((list) => setHits(list.slice(0, 8)))
        .catch(() => setHits([]))
        .finally(() => {
          setSearching(false);
          // The panel lives in a scrolling dialog body; make sure the results are on screen.
          resultsRef.current?.scrollIntoView({ block: 'nearest' });
        });
    }, 250);
    return () => clearTimeout(t);
  }, [search, itemId, rows]);

  /** Why a search hit cannot be picked, or null when it can. Shown on the row instead of hiding it. */
  const blocked = useCallback((dish: DineItem): string | null => {
    if (itemId && dish.id === itemId) return 'this is the dish itself';
    if (rows.some((r) => r.pairItemId === dish.id)) return 'already listed';
    return null;
  }, [itemId, rows]);

  const add = useCallback((dish: DineItem) => {
    if (rows.length >= MAX) {
      notificationService.info('Four at most', `Up to ${MAX} companions are shown. Remove one to add another.`);
      return;
    }
    setRows((p) => [...p, {
      id: '', pairItemId: dish.id, pairItemCode: dish.itemCode, pairItemName: dish.itemName,
      pairPrice: dish.price, pairIsOpenPrice: dish.isOpenPrice, pairIsSoldOut: dish.isSoldOut, pairIsActive: dish.isActive,
      displayOrder: (p.length + 1) * 10, isActive: true, rowVersion: 0,
    }]);
    setSearch(''); setHits([]); setDirty(true);
  }, [rows.length]);

  const move = useCallback((i: number, dir: -1 | 1) => {
    setRows((p) => {
      const j = i + dir;
      if (j < 0 || j >= p.length) return p;
      const next = [...p]; [next[i], next[j]] = [next[j], next[i]];
      return next.map((r, k) => ({ ...r, displayOrder: (k + 1) * 10 }));
    });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const body: PairInput[] = rows.map((r, i) => ({
        id: r.id || undefined, pairItemId: r.pairItemId, displayOrder: (i + 1) * 10, isActive: r.isActive,
      }));
      if (itemId) {
        const r = await dineOpsService.saveItemPairs(itemId, body);
        setRows(r.own); setGroupRows(r.group); setEffective(r.effective);
      } else if (groupId) {
        setRows(await dineOpsService.saveGroupPairs(groupId, body));
      }
      setDirty(false);
      notificationService.success('Saved', rows.length === 0 && itemId
        ? 'This dish now follows its menu group’s companions.'
        : `${rows.length} companion(s) saved.`);
      onChanged?.();
    } catch (err: any) {
      notificationService.error('Not saved', err?.response?.data?.message ?? 'The companions could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [rows, itemId, groupId, onChanged]);

  const following = itemId && rows.length === 0;
  const shown = useMemo(() => (following ? groupRows : rows), [following, groupRows, rows]);

  return (
    <>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
        {itemId ? (
          following
            ? <>This dish follows its menu group{groupName ? ` — ${groupName}` : ''}. Add a dish below to give it its own list; clear the list to follow the group again.</>
            : <>This dish has its own companions, not its menu group&rsquo;s.</>
        ) : (
          <>Every dish in this group is prompted with these, unless the dish has its own list.</>
        )}
        {' '}A prompt for the waiter, never an automatic addition. Up to {MAX} are shown; sold-out dishes are skipped.
      </div>

      {/* The search sits ABOVE the list: the dialog body scrolls, and a results box
          under the last field would open below the fold where nobody sees it. */}
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">Add a companion</label>
        <input
          type="text"
          className="form-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); const first = hits.find((h) => !blocked(h)); if (first) add(first); }
            if (e.key === 'Escape') { setSearch(''); setHits([]); }
          }}
          placeholder={rows.length >= MAX ? `Up to ${MAX} — remove one first` : 'Type a dish name or code, then click a result (or press Enter for the first)'}
          disabled={rows.length >= MAX || saving}
          autoComplete="off"
        />
        {search.trim().length >= 2 && (
          <div
            ref={resultsRef}
            role="listbox"
            style={{ border: '1px solid var(--border-color, #cbd5e1)', borderRadius: 6, marginTop: 4, maxHeight: 220, overflowY: 'auto', background: 'var(--bg-primary, #fff)' }}
          >
            {searching && hits.length === 0 && <div style={{ padding: '8px 10px', opacity: 0.7 }}>Searching…</div>}
            {!searching && hits.length === 0 && (
              <div style={{ padding: '8px 10px', opacity: 0.7 }}>No dish matches &ldquo;{search.trim()}&rdquo;.</div>
            )}
            {hits.map((h) => {
              const why = blocked(h);
              return (
                <div
                  key={h.id}
                  role="option"
                  aria-selected={false}
                  aria-disabled={!!why}
                  tabIndex={why ? -1 : 0}
                  onMouseDown={(e) => { e.preventDefault(); if (!why) add(h); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !why) add(h); }}
                  title={why ?? 'Add as a companion'}
                  style={{
                    padding: '8px 10px', cursor: why ? 'not-allowed' : 'pointer', opacity: why ? 0.55 : 1,
                    borderTop: '1px solid var(--border-color, #eee)', display: 'flex', justifyContent: 'space-between', gap: 8,
                  }}
                >
                  <span><strong>{h.itemName}</strong> <span style={{ opacity: 0.6 }}>{h.itemCode} · {h.groupName}</span></span>
                  <span style={{ opacity: 0.7 }}>{why ? `— ${why}` : `${h.isOpenPrice ? 'open priced' : h.price}${h.isSoldOut ? ' · sold out' : ''}`}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && <div style={{ opacity: 0.7 }}>Loading…</div>}

      {!loading && shown.length === 0 && (
        <div style={{ opacity: 0.7, marginBottom: 8 }}>No companions yet.</div>
      )}

      {!loading && shown.map((r, i) => (
        <div
          key={r.id || r.pairItemId}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 6,
            border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6,
            opacity: following ? 0.75 : 1,
          }}
        >
          <span style={{ width: 22, textAlign: 'right', opacity: 0.6 }}>{i + 1}.</span>
          <span style={{ flex: 1 }}>
            <strong>{r.pairItemName}</strong> <span style={{ opacity: 0.6 }}>{r.pairItemCode}</span>
            {' · '}{r.pairIsOpenPrice ? 'open priced' : r.pairPrice}
            {r.pairIsSoldOut && <span className="text-danger" style={{ marginLeft: 6, fontSize: 11 }}>sold out — not shown</span>}
            {!r.pairIsActive && <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.6 }}>off the card</span>}
          </span>
          {!following && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }} title="Off: kept, but not prompted">
                <input type="checkbox" checked={r.isActive}
                  onChange={(e) => { setRows((p) => p.map((x, k) => (k === i ? { ...x, isActive: e.target.checked } : x))); setDirty(true); }} />
                on
              </label>
              <button className="btn btn-secondary btn-sm" disabled={i === 0} onClick={() => move(i, -1)} title="Up">↑</button>
              <button className="btn btn-secondary btn-sm" disabled={i === shown.length - 1} onClick={() => move(i, 1)} title="Down">↓</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setRows((p) => p.filter((_, k) => k !== i)); setDirty(true); }} title="Remove">
                <IconSystem.trash size={14} />
              </button>
            </>
          )}
        </div>
      ))}

      {itemId && !following && effective.length !== rows.filter((r) => r.isActive).length && !dirty && (
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
          The waiter will be shown {effective.length} of these (sold-out and off-card dishes are skipped).
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => void save()} disabled={saving || loading || !dirty}>
          {saving ? 'Saving…' : 'Save companions'}
        </button>
        {dirty && <span style={{ fontSize: 12, opacity: 0.7 }}>Unsaved changes</span>}
      </div>
    </>
  );
};

export default DineOpsPairPanel;
